import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { ApiService } from "../../../Services/ApiService";
import logger from '../../../Services/LoggingService';
import { PositionSelection } from "../../Atom/PositionSelection/PositionSelection";
import { IUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { getUnitConfigurationFromConfigurationKey } from "../../../Utils/UnitConfigurationHelper";
import saveAs from "file-saver";
import { IBlindTest } from "../../../Models/IBlindTest";
import { BlindTestList } from "./BlindTestList";
import CancelIcon from '@mui/icons-material/Cancel';
import CodeIcon from '@mui/icons-material/Code';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';

/**
 * Blind test overview component for displaying the blind tests.
 *
 * Coordinates a single view-level loading sequence (config -> filters -> list)
 * together with `PositionSelection` via the `onConfigLoadingChange` callback,
 * mirroring the pattern used in `ModelerOverview` so the two dashboards stay
 * structurally consistent. Race conditions on rapid position switching are
 * mitigated with a monotonic fetch token so only the latest response is
 * committed to state.
 */
export const BlindTestOverview: React.FC = () => {
    const ctx = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);
    const theme = useTheme();
    const inputFile = createRef<HTMLInputElement>();

    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [loadingBlindTests, setLoadingBlindTests] = useState(false);
    const [configLoading, setConfigLoading] = useState(true);

    const [unitConfiguration, setUnitConfiguration] = useState<IUnitConfiguration | undefined>();
    const [fullConfiguration, setFullConfiguration] = useState<IUnitConfiguration | undefined>();
    const [blindTests, setBlindTests] = useState<IBlindTest[]>([]);

    const [csvFile, setCsvFile] = useState<File | undefined>();
    const [fileName, setFileName] = useState('Select csv file...');
    const [error, setError] = useState<string | null>(null);

    // Invalidates in-flight blind test fetches when configuration changes so a
    // stale response cannot overwrite a newer one during rapid position
    // switching.
    const blindTestFetchTokenRef = useRef(0);

    const styles = {
        container: {
            width: '100%',
        },
        headerStack: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            paddingInline: theme.spacing(2),
            paddingTop: theme.spacing(2),
        },
        titleSection: {
            flexGrow: 1,
        },
        title: {
            margin: theme.spacing(2),
            fontSize: '1.5rem',
            fontWeight: 'normal',
        },
        actionsSection: {
            margin: theme.spacing(2),
        },
        actionStack: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        loadingContainer: {
            display: 'flex',
            alignItems: 'center',
            padding: '5px',
        },
        fileButton: {
            textTransform: 'none',
        },
        uploadButton: {
            marginLeft: '10px',
            textTransform: 'none',
        },
        downloadButton: {
            marginLeft: '10px',
            textTransform: 'none',
        },
        centerLoadingContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            padding: 4
        },
        loadingText: {
            color: theme.palette.text.secondary
        },
        noDataContainer: {
            display: 'flex',
            justifyContent: 'center',
            padding: 4
        },
        noDataText: {
            color: theme.palette.text.secondary,
            textAlign: 'center'
        },
        // Mirrors PositionSelection's outer wrapping so the list aligns
        // horizontally with the filters above it.
        listOuter: {
            padding: 3,
        },
        listStack: {
            width: '100%',
            maxWidth: '98%',
            mx: 'auto',
        },
        listPaper: {
            padding: 3,
            width: '100%',
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[2],
        },
    } as const;

    /**
     * Fetches the full unit configuration required for upload/download
     * metadata (backend needs the expanded configuration for those endpoints).
     */
    const getFullConfig = useCallback(async (configuration: IUnitConfiguration) => {
        try {
            const configResponse = await apiService.getAsync('settings/unitconfiguration/alfalavalposition', configuration);
            if (!configResponse.ok) {
                throw new Error('Could not get complete configuration.');
            }
            const config = await configResponse.json();
            setFullConfiguration(config[0]);
        } catch (err) {
            logger.error("Failed to fetch full configuration", err as Error, {
                unitConfiguration: configuration
            }, {
                component: 'BlindTestOverview',
                method: 'getFullConfig'
            });
            setError(err instanceof Error ? err.message : 'Could not get complete configuration.');
        }
    }, [apiService]);

    /**
     * Fetches blind tests for the given configuration. Uses a monotonic token
     * so stale responses from obsolete positions are discarded.
     */
    const getBlindTestsAsync = useCallback(async (configuration: IUnitConfiguration) => {
        const token = ++blindTestFetchTokenRef.current;
        setLoadingBlindTests(true);
        try {
            const response = await apiService.getAsync(`blindtests`, configuration);
            if (!response.ok) {
                throw new Error('Could not fetch blind tests');
            }
            const result: IBlindTest[] = await response.json();
            if (token !== blindTestFetchTokenRef.current) return; // stale response
            setBlindTests(result);
        } catch (err) {
            if (token !== blindTestFetchTokenRef.current) return;
            const errorMessage = err instanceof Error ? err.message : 'Could not fetch blind tests';
            setError(errorMessage);
            logger.error("Failed to fetch blind tests", err as Error, null, {
                component: 'BlindTestOverview',
                method: 'getBlindTestsAsync'
            });
        } finally {
            if (token === blindTestFetchTokenRef.current) {
                setLoadingBlindTests(false);
            }
        }
    }, [apiService]);

    // Single source of truth for (re)loads driven by the position selection.
    // Avoids the previous duplicate-fetch pattern (handleChange + effect).
    useEffect(() => {
        if (!unitConfiguration) return;
        getFullConfig(unitConfiguration);
        getBlindTestsAsync(unitConfiguration);
    }, [unitConfiguration, getFullConfig, getBlindTestsAsync]);

    /**
     * Handle position selection change. Only updates state; fetching is
     * driven entirely by the effect above so we never request the same
     * data twice per selection.
     */
    const handleChange = (positionKey: string) => {
        const config = getUnitConfigurationFromConfigurationKey(positionKey);
        if (config) {
            setUnitConfiguration(config);
        }
    };

    /**
     * Handle file selection - just stores the first file from the picker.
     */
    const handleFileChange = (e: React.FormEvent<HTMLInputElement>) => {
        const uploadedFiles: FileList | null = e.currentTarget.files;
        if (!uploadedFiles || uploadedFiles.length === 0) return;

        const f = uploadedFiles[0];
        setFileName(f.name);
        setCsvFile(f);
    };

    /**
     * Upload the selected CSV file and refresh the blind test list.
     * Guard runs BEFORE setting the spinner so we never leave it stuck when
     * the preconditions are not met.
     */
    const submitCsv = async () => {
        if (csvFile === undefined || fullConfiguration === undefined) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", csvFile, csvFile.name);

            const response = await apiService.postAsync(formData, 'blindtests/csv', fullConfiguration);
            if (!response.ok) {
                throw new Error('Could not upload csv file. ' + await response.text());
            }

            await getBlindTestsAsync(fullConfiguration);
            clearCsv();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Could not upload csv file';
            setError(errorMessage);
            logger.error("Failed to upload CSV file", err as Error, {
                fileName: csvFile?.name,
                hasFullConfiguration: !!fullConfiguration
            }, {
                component: 'BlindTestOverview',
                method: 'submitCsv'
            });
        } finally {
            setUploading(false);
        }
    };

    /**
     * Converts a readable stream to a CSV blob and triggers a download.
     * Pure transport helper – intentionally does not touch any loading state
     * so callers can wrap it in a deterministic try/finally.
     */
    const convertStreamToCsv = async (stream: ReadableStream, filename: string) => {
        const reader = stream.getReader();
        let csvData = '';

        while (true) {
            const result = await reader.read();
            if (result.done) {
                break;
            }
            csvData += new TextDecoder().decode(result.value);
        }

        const blob = new Blob([csvData], { type: 'text/csv' });
        saveAs(blob, filename);
    };

    /**
     * Generates a csv template for the current configuration and downloads it.
     */
    const generateCSV = async () => {
        if (fullConfiguration === undefined) return;

        setDownloading(true);
        try {
            const response = await apiService.getAsync('blindtests/generatecsv', fullConfiguration);
            if (!response.ok) {
                throw new Error('Could not fetch csv sample. ' + response.statusText);
            }

            await convertStreamToCsv(
                response.body!,
                fullConfiguration.processStage + fullConfiguration.alfaLavalPosition + ".csv"
            );
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Could not fetch csv sample';
            setError(errorMessage);
            logger.error("Failed to generate CSV template", err as Error, {
                processStage: fullConfiguration?.processStage,
                alfaLavalPosition: fullConfiguration?.alfaLavalPosition
            }, {
                component: 'BlindTestOverview',
                method: 'generateCSV'
            });
        } finally {
            setDownloading(false);
        }
    };

    const uploadCsv = () => {
        inputFile.current?.click();
    };

    const clearCsv = () => {
        setCsvFile(undefined);
        setFileName('Select csv file...');
        if (inputFile.current !== null && inputFile.current !== undefined) {
            inputFile.current.value = '';
        }
    };

    const handleCloseError = () => {
        setError(null);
    };

    // Derived view state – single source of truth used across the render tree.
    const hasCompleteSelection = !!unitConfiguration?.alfaLavalPosition;
    const hasBlindTests = blindTests.length > 0;

    return (
        <Box sx={styles.container}>
            <Box sx={styles.headerStack}>
                <Box sx={styles.titleSection}>
                    <Typography variant="h4" component="h3" sx={styles.title}>
                        Blind tests
                    </Typography>
                </Box>

                <Box sx={styles.actionsSection}>
                    {fullConfiguration?.alfaLavalPosition && (
                        <Box sx={styles.actionStack}>
                            {(downloading || uploading) && (
                                <Box sx={styles.loadingContainer}>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    <Typography variant="body2">
                                        {downloading ? "Downloading" : "Uploading"}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    ref={inputFile}
                                    type='file'
                                    hidden
                                    accept='.csv'
                                    onChange={handleFileChange}
                                />
                                {fileName !== 'Select csv file...' && (
                                    <Tooltip title="Remove file">
                                        <IconButton
                                            size="small"
                                            onClick={clearCsv}
                                            sx={{ color: theme.palette.error.main }}
                                        >
                                            <CancelIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Button
                                    startIcon={<CodeIcon />}
                                    onClick={uploadCsv}
                                    disabled={downloading}
                                    sx={styles.fileButton}
                                    variant="text"
                                >
                                    {fileName}
                                </Button>
                            </Box>

                            {csvFile && (
                                <Button
                                    startIcon={<UploadIcon />}
                                    variant="outlined"
                                    onClick={submitCsv}
                                    disabled={uploading || loadingBlindTests || downloading}
                                    sx={styles.uploadButton}
                                >
                                    Upload blind test data
                                </Button>
                            )}

                            <Button
                                startIcon={<DownloadIcon />}
                                variant="contained"
                                color="primary"
                                disabled={downloading}
                                onClick={generateCSV}
                                sx={styles.downloadButton}
                            >
                                Download csv template
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Position Selection owns its own internal loading UX and reports
                the initial configuration phase upwards so we can coordinate a
                single view-level loading sequence: config -> filters -> list. */}
            <PositionSelection
                onChange={handleChange}
                onIncompleteUnitConfiguration={() => {
                    setBlindTests([]);
                    setUnitConfiguration(undefined);
                    setFullConfiguration(undefined);
                }}
                onConfigLoadingChange={setConfigLoading}
            />

            {/* Blind test list loading: shown only after filters are ready and a
                position is selected; replaces any list content while fetching. */}
            {!configLoading && hasCompleteSelection && loadingBlindTests && (
                <Box sx={styles.centerLoadingContainer}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" sx={styles.loadingText}>
                        Fetching blind tests...
                    </Typography>
                </Box>
            )}

            {/* Blind test list: only rendered once configuration + list are
                both ready. The wrapping mirrors PositionSelection's outer
                layout so the list is aligned with the filters above it. */}
            {!configLoading && hasCompleteSelection && !loadingBlindTests && fullConfiguration && hasBlindTests && (
                <Box sx={styles.listOuter}>
                    <Stack sx={styles.listStack}>
                        <Paper elevation={2} sx={styles.listPaper}>
                            <BlindTestList
                                unitConfiguration={fullConfiguration}
                                blindTests={blindTests}
                            />
                        </Paper>
                    </Stack>
                </Box>
            )}

            {!configLoading && hasCompleteSelection && !loadingBlindTests && !hasBlindTests && (
                <Box sx={styles.noDataContainer}>
                    <Typography variant="body1" sx={styles.noDataText}>
                        No blind tests available for the selected position
                    </Typography>
                </Box>
            )}

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error" variant="filled">
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};
